/**
 * Search Backend Factory
 *
 * Provides factory functions to get the appropriate search backend
 * based on the feature flag. This allows switching between Algolia
 * and ElasticSearch without changing consuming code.
 *
 * Pattern from: maple/frontend/strat/strat/search/backendUtils.ts
 */

import { isElasticBackend, isElasticBackendClient } from "./isElasticBackend"
import Algolia from "../Algolia"

/**
 * Check if ElasticSearch is enabled (works for both SSR and CSR)
 * @param {Object} [cookies={}] - Parsed cookies object (only needed for SSR)
 * @returns {boolean}
 */
const isElasticEnabled = (cookies = {}) => {
  if (typeof window === "undefined") {
    // Server-side: use passed cookies
    return isElasticBackend(cookies)
  }
  // Client-side: read document.cookie
  return isElasticBackendClient()
}

/**
 * Get the ads search index/searcher (works for both SSR and CSR)
 *
 * @param {string} [sort='popular'] - Sort option key
 * @param {Object} [cookies={}] - Parsed cookies object (only needed for SSR)
 * @returns {Object} Searcher instance (Algolia index or ES searcher)
 */
export const getAdsIndex = (sort = "popular", cookies = {}) => {
  if (isElasticEnabled(cookies)) {
    const { ElasticSearch } = require("../ElasticSearch")
    return ElasticSearch.createAdsSearcher(sort)
  }
  return Algolia.getAdsIndex(sort)
}

/**
 * Get the locations search index/searcher (works for both SSR and CSR)
 *
 * @param {Object} [cookies={}] - Parsed cookies object (only needed for SSR)
 * @returns {Object} Searcher instance (Algolia index or ES searcher)
 */
export const getLocationsIndex = (cookies = {}) => {
  if (isElasticEnabled(cookies)) {
    const { ElasticSearch } = require("../ElasticSearch")
    return ElasticSearch.createLocationsSearcher()
  }
  return Algolia.getLocationsIndex()
}

/**
 * Get the deleted/inactive ads index (Algolia only - not used in ES)
 * Note: This returns null when ES is enabled as deleted ads index is deprecated
 *
 * @param {Object} [cookies={}] - Parsed cookies object (only needed for SSR)
 * @returns {Object|null} Algolia index or null if ES enabled
 */
export const getDeletedAdsIndex = (cookies = {}) => {
  if (isElasticEnabled(cookies)) {
    // ES doesn't use a separate deleted ads index
    return null
  }
  return Algolia.getInactiveAdsIndex()
}

/**
 * Check if deleted ads should be fetched
 * Only needed for Algolia backend
 *
 * @param {Object} [cookies={}] - Parsed cookies object (only needed for SSR)
 * @returns {boolean} True if should fetch deleted ads (Algolia only)
 */
export const shouldFetchDeletedAds = (cookies = {}) => {
  return !isElasticEnabled(cookies)
}

/**
 * Get the current backend name (works for both SSR and CSR)
 *
 * @param {Object} [cookies={}] - Parsed cookies object (only needed for SSR)
 * @returns {string} 'elastic' or 'algolia'
 */
export const getBackendName = (cookies = {}) => {
  return isElasticEnabled(cookies) ? "elastic" : "algolia"
}
